import { useEffect, useState } from "react";
import { Contact, LogOut, Loader2, IndianRupee, Wallet, TrendingUp, Clock3, QrCode, Download, Copy } from "lucide-react";
import { QRCodeSVG, QRCodeCanvas } from "qrcode.react";
import { downloadReferralPoster } from "@/lib/referralPoster";
import { api, apiErr, inr } from "@/lib/api";
import { toast } from "sonner";

export default function AgentPortal() {
  const [agent, setAgent] = useState(null);
  const [checking, setChecking] = useState(true);
  const [form, setForm] = useState({ login_id: "", password: "" });
  const [busy, setBusy] = useState(false);

  const loadMe = () => api.get("/agent/summary").then((r) => { setAgent(r.data); }).catch(() => setAgent(null)).finally(() => setChecking(false));
  useEffect(() => { loadMe(); }, []);

  const login = async (e) => {
    e?.preventDefault();
    if (!form.login_id.trim() || !form.password) return toast.error("Login ID aur password dono dein");
    setBusy(true);
    try {
      await api.post("/agent/login", { login_id: form.login_id.trim(), email: form.login_id.trim(), password: form.password });
      await loadMe();
      toast.success("Login successful");
    } catch (err) { toast.error(apiErr(err)); } finally { setBusy(false); }
  };
  const logout = async () => { try { await api.post("/agent/logout"); } catch (e) { console.warn("agent logout failed", e); } setAgent(null); setForm({ login_id: "", password: "" }); };

  if (checking) return <div className="min-h-screen flex items-center justify-center text-slate-400" data-testid="agent-portal-loading">Loading…</div>;

  if (!agent) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4" data-testid="agent-login-page">
        <div className="w-full max-w-sm bg-white rounded-2xl p-6 shadow-xl">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-11 h-11 rounded-xl bg-teal-600 text-white flex items-center justify-center"><Contact size={22} /></div>
            <div><h1 className="text-lg font-extrabold text-slate-900">Agent Portal</h1><p className="text-xs text-slate-500">Rani Super Market — commission agent login</p></div>
          </div>
          <form onSubmit={login} className="space-y-3">
            <input data-testid="agent-portal-loginid" placeholder="Login ID / Email" value={form.login_id} onChange={(e) => setForm({ ...form, login_id: e.target.value })} className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm" />
            <input data-testid="agent-portal-password" type="password" placeholder="Password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm" />
            <button data-testid="agent-portal-login-btn" type="submit" disabled={busy} className="w-full py-2.5 bg-teal-600 hover:bg-teal-700 text-white rounded-lg font-bold disabled:opacity-60 inline-flex items-center justify-center gap-2">{busy && <Loader2 size={16} className="animate-spin" />} Login</button>
          </form>
        </div>
      </div>
    );
  }

  const Stat = ({ icon: Icon, label, val, cls }) => (
    <div className={`rounded-2xl p-5 ${cls}`}><Icon size={22} className="opacity-80" /><p className="text-xs font-semibold opacity-80 mt-2">{label}</p><p className="text-2xl font-extrabold mt-0.5">{val}</p></div>
  );

  return (
    <div className="min-h-screen bg-slate-100" data-testid="agent-dashboard">
      <header className="bg-slate-900 text-white px-4 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3"><div className="w-9 h-9 rounded-lg bg-teal-600 flex items-center justify-center"><Contact size={18} /></div><div><p className="font-bold leading-none">{agent.name}</p><p className="text-[11px] text-teal-300">{agent.login_id || agent.email}</p></div></div>
        <button data-testid="agent-portal-logout" onClick={logout} className="inline-flex items-center gap-1.5 text-sm font-semibold bg-slate-800 hover:bg-slate-700 px-3 py-2 rounded-lg"><LogOut size={15} /> Logout</button>
      </header>
      <main className="max-w-3xl mx-auto p-4 md:p-6 space-y-5">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Stat icon={TrendingUp} label="Total Commission" val={inr(agent.total_commission)} cls="bg-teal-600 text-white" />
          <Stat icon={Clock3} label="Awaiting Approval" val={inr(agent.pending || 0)} cls="bg-violet-600 text-white" />
          <Stat icon={Wallet} label="Balance Due" val={inr(agent.balance)} cls="bg-amber-500 text-white" />
          <Stat icon={IndianRupee} label="Paid Out" val={inr(agent.paid)} cls="bg-emerald-600 text-white" />
        </div>
        {agent.login_id && (
          <div className="bg-white rounded-2xl border border-slate-200 p-4" data-testid="agent-referral-card">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="min-w-0">
                <h2 className="font-bold text-slate-800 flex items-center gap-2"><QrCode size={18} className="text-teal-600" /> Aapka Referral QR</h2>
                <p className="text-xs text-slate-500 mt-1 max-w-md">Ye QR / poster share karein. Koi bhi ise scan karke order karega to commission seedhe aapko milega (owner approval ke baad).</p>
                <div className="mt-2 text-sm text-slate-600">Referral code: <span className="font-black text-teal-700">{agent.login_id}</span></div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <button data-testid="agent-poster-download" onClick={() => downloadReferralPoster({ qrCanvasId: "agent-ref-qr", agentName: agent.name, code: agent.login_id })} className="inline-flex items-center gap-1.5 bg-teal-600 hover:bg-teal-700 text-white text-xs font-bold px-3 py-2 rounded-lg active:scale-95 transition-transform"><Download size={14} /> Download Poster</button>
                  <button data-testid="agent-copy-link" onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/?ref=${agent.login_id}`); toast.success("Referral link copy ho gaya"); }} className="inline-flex items-center gap-1.5 border border-slate-200 text-slate-600 text-xs font-bold px-3 py-2 rounded-lg hover:bg-slate-50"><Copy size={14} /> Copy Link</button>
                </div>
              </div>
              <div className="bg-white rounded-xl border border-slate-100 p-2 shrink-0">
                <QRCodeSVG value={`${window.location.origin}/?ref=${agent.login_id}`} size={116} level="M" marginSize={1} fgColor="#0f766e" imageSettings={{ src: "/rani-logo.png", height: 24, width: 24, excavate: true }} />
              </div>
            </div>
            <div style={{ position: "absolute", left: -9999, top: -9999 }} aria-hidden>
              <QRCodeCanvas id="agent-ref-qr" value={`${window.location.origin}/?ref=${agent.login_id}`} size={480} level="M" marginSize={1} fgColor="#0f766e" imageSettings={{ src: "/rani-logo.png", height: 72, width: 72, excavate: true }} />
            </div>
          </div>
        )}
        <div className="bg-white rounded-2xl border border-slate-200 p-4">
          <h2 className="font-bold text-slate-800 mb-2">Payment history</h2>
          {(!agent.payments || agent.payments.length === 0) ? (
            <p className="text-sm text-slate-400" data-testid="agent-payments-empty">Abhi koi payment nahi.</p>
          ) : (
            <div className="divide-y divide-slate-50" data-testid="agent-payments-list">
              {agent.payments.map((p) => (
                <div key={p.id} className="flex items-center justify-between py-2 text-sm"><span className="text-slate-500">{String(p.created_at).slice(0, 10)}</span><span className="font-bold text-emerald-600">{inr(p.amount)}</span></div>
              ))}
            </div>
          )}
        </div>
        <p className="text-xs text-slate-400 text-center">Commission app aur website — dono me same. UPI: {agent.upi_id || "—"}</p>
      </main>
    </div>
  );
}
